import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../core/components/Button';
import { Input, Label } from '../../../core/components/FormElements';
import { Modal } from '../../../core/components/Modal';
import { XuiExportClient } from './types';
import './AddXuiClientsDialog.css';

interface AddXuiClientsDialogProps {
    onClose: () => void;
    onConfirm: (clients: XuiExportClient[]) => void;
}

interface ClientForm {
    email: string;
    expiryTime: string;
    reset: string;
    enable: boolean;
}

const emptyForm: ClientForm = {
    email: '',
    expiryTime: '',
    reset: '',
    enable: true,
};

export const AddXuiClientsDialog: React.FC<AddXuiClientsDialogProps> = ({ onClose, onConfirm }) => {
    const [form, setForm] = useState<ClientForm>(emptyForm);
    const [clientsStack, setClientsStack] = useState<XuiExportClient[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleAddToStack = () => {
        if (!form.email.trim()) { setError('Email обязателен'); return; }
        if (!form.expiryTime) { setError('Дата окончания обязательна'); return; }

        const newClient: XuiExportClient = {
            email: form.email.trim(),
            expiryTime: new Date(form.expiryTime).getTime(),
            reset: form.reset ? parseInt(form.reset, 10) : 0,
            enable: form.enable,
        };

        setClientsStack(prev => [...prev, newClient]);
        setForm(emptyForm);
        setError(null);
    };

    const handleRemoveFromStack = (index: number) => {
        setClientsStack(prev => prev.filter((_, i) => i !== index));
    };

    const handleConfirm = () => {
        if (clientsStack.length === 0) { setError('Добавьте хотя бы одного пользователя'); return; }
        onConfirm(clientsStack);
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddToStack(); }
    };

    return (
        <Modal onClose={onClose} className="axc-dialog">
            <div className="axc-header">
                <h3>Добавить пользователей XUI</h3>
                <Button variant="ghost" size="sm" iconOnly icon={<X size={16} />} onClick={onClose} aria-label="Закрыть" />
            </div>

            <div className="axc-form">
                <div className="axc-field">
                    <Label>Email</Label>
                    <Input
                        type="email"
                        value={form.email}
                        onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                        onKeyDown={handleKeyDown}
                        placeholder="user@example.com"
                    />
                </div>

                <div className="axc-field">
                    <Label>Дата окончания</Label>
                    <Input
                        type="datetime-local"
                        value={form.expiryTime}
                        onChange={e => setForm(prev => ({ ...prev, expiryTime: e.target.value }))}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <div className="axc-field">
                    <Label>Reset (дней)</Label>
                    <Input
                        type="number"
                        min="0"
                        value={form.reset}
                        onChange={e => setForm(prev => ({ ...prev, reset: e.target.value }))}
                        onKeyDown={handleKeyDown}
                        placeholder="0"
                    />
                </div>

                <div className="axc-field-checkbox">
                    <label>
                        <input
                            type="checkbox"
                            checked={form.enable}
                            onChange={e => setForm(prev => ({ ...prev, enable: e.target.checked }))}
                        />
                        Активен
                    </label>
                </div>

                <Button type="button" variant="secondary" onClick={handleAddToStack}>
                    Добавить в список
                </Button>

                {error && <div className="axc-error">{error}</div>}
            </div>

            {clientsStack.length > 0 && (
                <div className="axc-stack">
                    <div className="axc-stack-header">
                        <span>Добавлено пользователей: {clientsStack.length}</span>
                    </div>
                    <div className="axc-stack-list">
                        {clientsStack.map((client, index) => (
                            <div key={index} className="axc-stack-item">
                                <div className="axc-stack-info">
                                    <span className="axc-stack-email">{client.email}</span>
                                    <span className="axc-stack-meta">
                                        до {new Date(client.expiryTime!).toLocaleDateString()}
                                        {client.reset ? ` • reset: ${client.reset}` : ''}
                                        {!client.enable && ' • неактивен'}
                                    </span>
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    iconOnly
                                    icon={<X size={14} />}
                                    onClick={() => handleRemoveFromStack(index)}
                                    aria-label="Удалить"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="axc-actions">
                <Button type="button" variant="secondary" onClick={onClose}>Отмена</Button>
                <Button
                    type="button"
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={clientsStack.length === 0}
                >
                    ОК ({clientsStack.length})
                </Button>
            </div>
        </Modal>
    );
};
